import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReportObject, reportStatusOptions } from '../models/report';
import { MessageHandlerService } from '../services/message-handler.service';
import { UserDataManager } from '../user/UserDataManager';
import { User } from '../models/user';
import { Table } from 'primeng/table';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { ProgressBarService } from '../services/progress-bar.service';
import { Subject, Subscription, debounceTime, distinctUntilChanged, throttleTime } from 'rxjs';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  DEFAULT_SEARCH_MIN_CHARACTER,
  rowsPerPageOptions,
  NO_OF_RECORDS_PER_PAGE,
  ERROR_MESSAGES, DATE_PICKER_FORMAT, DATE_PICKER_PLACEHOLDER, reportDateAndTimeFormat,
  REPORT_DELETED_ERROR_MESSAGE,
} from '../common/Constants';
import { endOfToday } from '../common/calendar-max-date.util';
import { Paginator } from 'primeng/paginator';
import { UtilService } from '../services/util/util.service';
import { LocalStorageService } from '../services/local-storage.service';
import { ReportsService } from '../services/reports/reports.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit, OnDestroy {
  currentUser: User;

  // table and pagination variables
  pageNo: number = 0;
  noOfRecordsPerRow: number = NO_OF_RECORDS_PER_PAGE;
  rowsPerPageOptions: number[] = rowsPerPageOptions;
  isChangingPage: boolean = false;
  @ViewChild('paginator') paginator: Paginator;
  @ViewChild('dt') table: Table;
  totalReports: number = 0;
  reportsList: ReportObject[] = [];
  // search variables
  searchInput: string = '';
  defaultSearchMinCharacter = DEFAULT_SEARCH_MIN_CHARACTER;
  private searchInputSubject: Subject<string> = new Subject<string>();

  // date range filter
  rangeDates: Date[] | null = null;

  // additional filters
  selectedReportType: string = '';
  selectedStatus: string = '';
  reportTypeOptions: SelectItem[] = [
    {label: 'All Types', value: ''},
    {label: 'Patient Summary', value: 'patient-summary'},
    {label: 'Agency Performance', value: 'agency-performance'},
    {label: 'Financial Report', value: 'financial'},
    {label: 'Compliance Report', value: 'compliance'},
  ];
  statusFilterOptions: SelectItem[] = [
    {label: 'All Status', value: ''},
    {label: 'Pending', value: 'Pending'},
    {label: 'Processing', value: 'Processing'},
    {label: 'Completed', value: 'Completed'},
    {label: 'Processed', value: 'Processed'},
    {label: 'Failed', value: 'Failed'},
  ];

  subscriptions: Subscription[] = [];
  reportStatusOptions = reportStatusOptions;

  // share report sidebar state
  shareSidebarVisible: boolean = false;
  shareEmailForm: FormGroup;
  isSubmittingShare: boolean = false;
  selectedReportForShare: ReportObject | null = null;
  loading: boolean = false;
  datePickerFormat = DATE_PICKER_FORMAT;
  datePickerPlaceholder = DATE_PICKER_PLACEHOLDER;
  showPassword = false;
  dateAndTimeFormat = reportDateAndTimeFormat;
  maxDate: Date = endOfToday();

  constructor(
    private messageHandlerService: MessageHandlerService,
    private userDataManager: UserDataManager,
    private readonly progressBarService: ProgressBarService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private utilService: UtilService,
    private localStorageService: LocalStorageService,
    private reportsService: ReportsService,
    private formBuilder: FormBuilder,
    private confirmationService: ConfirmationService,
  ) {
    this.matIconRegistry.addSvgIcon(
      'download',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/images/download-icon.svg',
      ),
    );
    this.matIconRegistry.addSvgIcon(
      'refresh',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/images/refresh-icon.svg',
      ),
    );

    this.shareEmailForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  ngOnInit(): void {
    this.initializeShareForm();
    // Debounced + throttled search subscription
    this.subscriptions.push(
      this.searchInputSubject
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          throttleTime(500)
        )
        .subscribe(() => this.executeSearch()),
    );
    this.subscriptions.push(
      this.userDataManager.user$.subscribe({
        next: (user) => {
          if (user) {
            this.currentUser = user;
            this.getReportsList();
          }
        },
        error: (error) => {
          this.messageHandlerService.generateToastMessage(
            'error',
            ERROR_MESSAGES.USER_PROFILE_FETCH,
            error.message || ERROR_MESSAGES.USER_PROFILE_FETCH,
          );
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  performSearch() {
    this.executeSearch();
  }

  onSearchKeyup() {
    this.searchInputSubject.next(this.searchInput ?? '');
  }

  private executeSearch() {
    if (this.searchInput === undefined || this.searchInput === null || this.searchInput === "") {
      this.getReportsList();
    } else if (this.searchInput && this.searchInput.length >= this.defaultSearchMinCharacter) {
      this.pageNo = 0;
      this.getReportsList();
    }
  }

  onCalendarClear() {
    this.searchInput = '';
    this.rangeDates = null;
    this.selectedReportType = '';
    this.selectedStatus = '';
    this.pageNo = 0;
    this.getReportsList();
  }

  clearAllFilters() {
    this.searchInput = '';
    this.rangeDates = null;
    this.selectedReportType = '';
    this.selectedStatus = '';
    this.pageNo = 0;
    this.getReportsList();
  }

  hasActiveFilters(): boolean {
    return !!(
      (this.searchInput && this.searchInput.trim().length > 0) ||
      (this.rangeDates && this.rangeDates.length > 0) ||
      (this.selectedReportType && this.selectedReportType.trim().length > 0) ||
      (this.selectedStatus && this.selectedStatus.trim().length > 0)
    );
  }

  paginate(event) {
    if (!this.isChangingPage) {
      this.utilService.setDataInLocalStorage(
        'globalPageLimit',
        'perPage',
        event.rows,
      );
      this.pageNo = event.page;
      this.noOfRecordsPerRow = event.rows;
      this.getReportsList();
    }
  }

  changePage() {
    this.isChangingPage = true;
    setTimeout(() => {
      this.paginator.changePage(this.pageNo);
      this.isChangingPage = false;
    });
  }

  getReportsList() {
    try {
      this.progressBarService.setProgressBarVisibility(true);
      const payload: any = {
        pagination: {
          page: this.pageNo + 1,
          limit: this.noOfRecordsPerRow,
        },
        search: this.searchInput?.trim() || '',
        startDate:
          this.rangeDates && this.rangeDates[0] ? this.utilService.convertDateFilterToISOString(this.rangeDates[0], true) : null,
        endDate:
          this.rangeDates && this.rangeDates[1] ? this.utilService.convertDateFilterToISOString(this.rangeDates[1], false) : null,
        reportType: this.selectedReportType?.trim() || '',
        status: this.selectedStatus?.trim() || '',
      };

      this.subscriptions.push(
        this.reportsService.getReportsList(payload).subscribe({
          next: (res) => {
            if (res?.success) {
              const reports:any = res.data.map((report) => {
                report.class = this.getStatusClass(report.reportStatus)
                report.icon = this.getStatusIcon(report.reportStatus),
                report.searchByDate = this.formatDateRange(report.searchByDate)

                return {
                  ...report
                }
              });
              this.totalReports = res.pagination?.total;
              this.reportsList = res.pagination?.total !== 0 ? reports: [];
            } else {
              throw new Error('Failed to fetch reports');
            }
            this.progressBarService.setProgressBarVisibility(false);
          },
          error: (error) => this.handleError(error),
        }),
      );
    } catch (error) {
      this.handleError(error);
    }
  }

  onDownloadReport(event: Event, report: ReportObject) {
    event.stopPropagation();
    const {s3Key, reportName} = report;

    const status = (report.reportStatus)?.toLowerCase();
    if (status.toLowerCase() !== 'processed') {
      this.messageHandlerService.generateToastMessage(
        'warn',
        'Download Not Available',
        'This report is not ready for download yet.',
      );
      return;
    }

    // Set loading state for this specific report
    const reportId = report.id;
    const index = this.reportsList.findIndex(
      (item) => (item.id || item._id) === reportId,
    );
    if (index !== -1) {
      this.reportsList[index] = {
        ...this.reportsList[index],
        isDownloading: true,
      };
    }

    const downloadPayload = {
      s3Key: s3Key,
      reportName: reportName,
      email: this.currentUser?.email || '',
    };

    const resetDownloadingState = () => {
      if (index !== -1) {
        this.reportsList[index] = {
          ...this.reportsList[index],
          isDownloading: false,
        };
      }
    };

    this.reportsService
      .downloadReport(downloadPayload)
      // Ensure loading state is always reset (success OR error)
      .pipe(finalize(() => resetDownloadingState()))
      .subscribe({
        next: (res) => {
          if (res?.success) {
            // Simulate file download
            const link = document.createElement('a');
            link.href = res.content?.url;
            link.download = report.reportName;
            link.target = '_blank';
            link.click();
          } else {
            throw new Error(res?.errorMessage || 'Failed to download report');
          }
        },
        error: (error) => {
          this.handleError(error, 'Failed to download report');
        },
      });
  }

  onDeleteReport(event: Event, report: ReportObject) {
    event.stopPropagation();
    const reportId = report.id;
    if (!reportId) {
      this.messageHandlerService.generateToastMessage(
        'error',
        'Delete Failed',
        'Invalid report identifier.',
      );
      return;
    }

    this.confirmationService.confirm({
      header: 'Confirm Delete',
      message: `Are you sure you want to delete "${report.reportName}"?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        try {
          const index = this.reportsList.findIndex(
            (item) => item.id === reportId,
          );
          if (index !== -1) {
            this.reportsList[index] = {
              ...this.reportsList[index],
              isDeleting: true,
            } as ReportObject;
          }

          this.reportsService.deleteReport(reportId).subscribe({
            next: (res) => {
              if (res?.statusCode === 200) {
                this.messageHandlerService.generateToastMessage(
                  'success',
                  'Report Deleted',
                  `${report.reportName} has been successfully deleted.`,
                );
                this.getReportsList();
              } else {
                this.handleError(new Error(REPORT_DELETED_ERROR_MESSAGE), REPORT_DELETED_ERROR_MESSAGE);
              }
            },
            error: (error) => this.handleError(error, REPORT_DELETED_ERROR_MESSAGE),
            complete: () => {
              if (index !== -1) {
                this.reportsList[index] = {
                  ...this.reportsList[index],
                  isDeleting: false,
                } as ReportObject;
              }
            },
          });
        } catch (error) {
          this.handleError(error, 'Failed to delete report');
        }
      },
    });
  }

  getStatusClass(status: string): string {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'completed':
      case 'processed':
        return 'text-green-600';
      case 'processing':
        return 'text-blue-600';
      case 'failed':
        return 'text-red-600';
      case 'deleted':
        return 'text-red-600';
      case 'pending':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  }

  getStatusIcon(status: string): string {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'completed':
      case 'processed':
        return 'pi-check-circle';
      case 'processing':
        return 'pi-spin pi-spinner';
      case 'failed':
        return 'pi-times-circle';
      case 'deleted':
        return 'pi-trash';
      case 'pending':
        return 'pi-clock';
      default:
        return 'pi-question-circle';
    }
  }

  formatDateRange(dateRange: any): string {
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
      return 'N/A';
    }

    const startDate = new Date(dateRange.startDate).toLocaleDateString(
      'en-US',
      {month: 'short', day: 'numeric', year: 'numeric'},
    );
    const endDate = new Date(dateRange.endDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${startDate} - ${endDate}`;
  }

  private handleError(
    error: any,
    customMessage: string = ERROR_MESSAGES.COMMUNICATION,
  ) {
    this.progressBarService.setProgressBarVisibility(false);
    this.messageHandlerService.generateToastMessage(
      'error',
      customMessage,
      error?.errorMessage || error?.message || error,
    );
  }

  private initializeShareForm() {
    this.shareEmailForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  onOpenShareSidebar(event: Event, report: ReportObject) {
    event.stopPropagation();
    const status = (report.reportStatus)?.toLowerCase();
    if (status !== 'processed') {
      this.messageHandlerService.generateToastMessage(
        'warn',
        'Share Not Available',
        'This report is not ready for sharing yet.',
      );
      return;
    }
    this.selectedReportForShare = report;
    this.shareEmailForm.reset();
    this.shareSidebarVisible = true;
  }

  onCloseShareSidebar() {
    this.shareSidebarVisible = false;
    this.selectedReportForShare = null;
    this.isSubmittingShare = false;
    this.shareEmailForm.reset();
  }

  onSubmitShare() {
    if (!this.selectedReportForShare) {
      return;
    }

    if (this.shareEmailForm.invalid) {
      this.shareEmailForm.markAllAsTouched();
      return;
    }

    try {
      this.isSubmittingShare = true;
      const email: string = this.shareEmailForm.value.email?.trim();
      const fileName: string = this.selectedReportForShare.s3Key || '';

      const payload = {email, fileName};
      this.reportsService.shareReport(payload).subscribe({
        next: (res: any) => {
          if (res?.statusCode === 200) {
            this.messageHandlerService.generateToastMessage(
              'success',
              'Report Shared',
              'Report share link has been sent successfully.',
            );
            this.onCloseShareSidebar();
          } else {
            //throw new Error(res?.errorMessage || 'Failed to share report');
          }
        },
        error: (error) => this.handleError(error, 'Failed to share report'),
        complete: () => (this.isSubmittingShare = false),
      });
    } catch (error) {
      this.isSubmittingShare = false;
      this.handleError(error, 'Failed to share report');
    }
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  togglePasswordVisibility(row: ReportObject) {
    row.showPassword = !row.showPassword;
  }

  copyPassword(password: string) {
    navigator.clipboard.writeText(password).then(() => {
      console.log('Password copied!');
      // You can also show a toast here if using PrimeNG ToastService
      this.messageHandlerService.generateToastMessage('success','Password copied!','Password copied!')
    }).catch((error) => {
      console.error('Failed to copy password to clipboard:', error);
      this.messageHandlerService.generateToastMessage('error','Copy Failed','Failed to copy password to clipboard');
    });
  }

  onRegenerate($event: MouseEvent, rowData: any) {
    $event.stopPropagation();
    const status = (rowData.reportStatus)?.toLowerCase();
    if (status === 'failed') {
      this.messageHandlerService.generateToastMessage(
        'warn',
        'Regenerate Not Available',
        'This report cannot be regenerated.',
      );
      return;
    }

    // Set loading state for this specific report
    const reportId = rowData.id;
    const index = this.reportsList.findIndex(
      (item) => (item.id || item._id) === reportId,
    );
    if (index !== -1) {
      this.reportsList[index] = {
        ...this.reportsList[index]
      };
    }

    this.reportsService.regenerateReport(rowData.id).subscribe({
      next: (res) => {
        if (res?.success) {
          this.messageHandlerService.generateToastMessage(
            'success',
            'Report Regenerated',
            'The report has been successfully regenerated.',
          );
          // Optionally, refresh the report list or update the specific report
          this.getReportsList();
        } else {
          //throw new Error(res?.errorMessage || 'Failed to regenerate report');
          this.messageHandlerService.generateToastMessage(
            'error',
            'Report Regenerated failed',
            res.message || 'Failed to regenerate report',
          );
        }
      },
      error: (error) => {
        //this.handleError(error, 'Failed to regenerate report');
        throw new Error(error?.errorMessage || error?.message || 'Failed to regenerate report');
      },
      complete: () => {
        // Reset loading state
        if (index !== -1) {
          this.reportsList[index] = {
            ...this.reportsList[index],
          };
        }
      },
    });
  }
}
